import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Plus,
  Trash2,
  Edit,
  ExternalLink,
  Star,
  TrendingUp,
  Eye,
  MousePointer,
  ThumbsUp,
  ThumbsDown,
  Globe,
  Twitter,
  Instagram,
  Newspaper,
  RefreshCw,
  Search,
  Filter
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const sourceIcons = {
  twitter: Twitter,
  instagram: Instagram,
  website: Globe,
  reddit: Globe,
  youtube: Globe,
  tiktok: Globe,
  facebook: Globe,
  rss: Newspaper
};

const qualityColors = {
  verified: 'bg-green-100 text-green-800',
  trusted: 'bg-blue-100 text-blue-800',
  community: 'bg-amber-100 text-amber-800',
  unverified: 'bg-gray-100 text-gray-800'
};

const statusColors = {
  active: 'bg-green-100 text-green-800',
  inactive: 'bg-gray-100 text-gray-800',
  suspended: 'bg-red-100 text-red-800',
  pending_review: 'bg-amber-100 text-amber-800'
};

const ExternalSourcesAdmin = () => {
  const { token } = useAuth();
  const [sources, setSources] = useState([]);
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('sources');
  const [categories, setCategories] = useState({
    petTypes: [],
    contentTypes: [],
    sourceTypes: [],
    qualityLevels: []
  });
  
  // Filters
  const [filters, setFilters] = useState({
    pet_type: '',
    content_type: '',
    source_type: '',
    quality: '',
    status: 'active'
  });
  
  // New source form
  const [showAddSource, setShowAddSource] = useState(false);
  const [newSource, setNewSource] = useState({
    name: '',
    description: '',
    source_type: 'website',
    source_url: '',
    source_handle: '',
    pet_types: ['general'],
    content_types: [],
    quality: 'community',
    has_permission: false,
    attribution_required: true,
    backlink_required: true,
    logo_url: '',
    notes: ''
  });
  
  // New content form
  const [showAddContent, setShowAddContent] = useState(false);
  const [newContent, setNewContent] = useState({
    source_id: '',
    external_id: '',
    content_type: 'cute',
    title: '',
    text: '',
    image_url: '',
    original_url: '',
    author_name: '',
    author_handle: ''
  });

  const fetchCategories = useCallback(async () => {
    try {
      const [petTypes, contentTypes, sourceTypes, qualityLevels] = await Promise.all([
        fetch(`${API_URL}/api/external-sources/categories/pet-types`).then(r => r.json()),
        fetch(`${API_URL}/api/external-sources/categories/content-types`).then(r => r.json()),
        fetch(`${API_URL}/api/external-sources/categories/source-types`).then(r => r.json()),
        fetch(`${API_URL}/api/external-sources/categories/quality-levels`).then(r => r.json())
      ]);
      setCategories({ petTypes, contentTypes, sourceTypes, qualityLevels });
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, []);

  const fetchSources = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      
      const response = await fetch(`${API_URL}/api/external-sources/sources?${params}`);
      if (response.ok) {
        const data = await response.json();
        setSources(data);
      }
    } catch (error) {
      console.error('Error fetching sources:', error);
    }
  }, [filters]);

  const fetchContent = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/external-sources/content?limit=50`);
      if (response.ok) {
        const data = await response.json();
        setContent(data);
      }
    } catch (error) {
      console.error('Error fetching content:', error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchCategories();
      await Promise.all([fetchSources(), fetchContent()]);
      setLoading(false);
    };
    loadData();
  }, [fetchCategories, fetchSources, fetchContent]);

  const handleAddSource = async () => {
    try {
      const response = await fetch(`${API_URL}/api/external-sources/sources`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newSource)
      });
      
      if (response.ok) {
        toast.success('Source added successfully');
        setShowAddSource(false);
        setNewSource({
          name: '',
          description: '',
          source_type: 'website',
          source_url: '',
          source_handle: '',
          pet_types: ['general'],
          content_types: [],
          quality: 'community',
          has_permission: false,
          attribution_required: true,
          backlink_required: true,
          logo_url: '',
          notes: ''
        });
        fetchSources();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add source');
      }
    } catch (error) {
      toast.error('Error adding source');
    }
  };

  const handleAddContent = async () => {
    try {
      const response = await fetch(`${API_URL}/api/external-sources/content`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newContent)
      });
      
      if (response.ok) {
        toast.success('Content added successfully');
        setShowAddContent(false);
        setNewContent({
          source_id: '',
          external_id: '',
          content_type: 'cute',
          title: '',
          text: '',
          image_url: '',
          original_url: '',
          author_name: '',
          author_handle: ''
        });
        fetchContent();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add content');
      }
    } catch (error) {
      toast.error('Error adding content');
    }
  };

  const handleDeleteSource = async (sourceId) => {
    if (!confirm('Delete this source and all its content?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/external-sources/sources/${sourceId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Source deleted');
        fetchSources();
        fetchContent();
      }
    } catch (error) {
      toast.error('Error deleting source');
    }
  };

  const handleToggleFeature = async (contentId) => {
    try {
      const response = await fetch(`${API_URL}/api/external-sources/content/${contentId}/feature`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.is_featured ? 'Content featured' : 'Content unfeatured');
        fetchContent();
      }
    } catch (error) {
      toast.error('Error toggling feature');
    }
  };

  const handleDeleteContent = async (contentId) => {
    try {
      const response = await fetch(`${API_URL}/api/external-sources/content/${contentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Content deleted');
        fetchContent();
      }
    } catch (error) {
      toast.error('Error deleting content');
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8" data-testid="external-sources-admin">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-heading font-medium">External Content Sources</h1>
          <p className="text-muted-foreground">Manage news feeds, social accounts, and curated content</p>
        </div>
        
        <div className="flex gap-2">
          <Dialog open={showAddSource} onOpenChange={setShowAddSource}>
            <DialogTrigger asChild>
              <Button data-testid="add-source-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Source
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Add External Source</DialogTitle>
              </DialogHeader>
              
              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Source Name *</Label>
                    <Input
                      value={newSource.name}
                      onChange={(e) => setNewSource({...newSource, name: e.target.value})}
                      placeholder="e.g., WeRateDogs"
                    />
                  </div>
                  <div>
                    <Label>Source Type *</Label>
                    <Select
                      value={newSource.source_type}
                      onValueChange={(v) => setNewSource({...newSource, source_type: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.sourceTypes.map(t => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div>
                  <Label>Source URL *</Label>
                  <Input
                    value={newSource.source_url}
                    onChange={(e) => setNewSource({...newSource, source_url: e.target.value})}
                    placeholder="https://twitter.com/dog_rates"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Handle (optional)</Label>
                    <Input
                      value={newSource.source_handle}
                      onChange={(e) => setNewSource({...newSource, source_handle: e.target.value})}
                      placeholder="@dog_rates"
                    />
                  </div>
                  <div>
                    <Label>Quality Level</Label>
                    <Select
                      value={newSource.quality}
                      onValueChange={(v) => setNewSource({...newSource, quality: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.qualityLevels.map(q => (
                          <SelectItem key={q.value} value={q.value}>{q.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={newSource.description}
                    onChange={(e) => setNewSource({...newSource, description: e.target.value})}
                    placeholder="Brief description of this source..."
                  />
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div className="flex items-center space-x-2">
                    <Switch
                      checked={newSource.has_permission}
                      onCheckedChange={(c) => setNewSource({...newSource, has_permission: c})}
                    />
                    <Label>Has Permission</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      checked={newSource.attribution_required}
                      onCheckedChange={(c) => setNewSource({...newSource, attribution_required: c})}
                    />
                    <Label>Attribution Required</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      checked={newSource.backlink_required}
                      onCheckedChange={(c) => setNewSource({...newSource, backlink_required: c})}
                    />
                    <Label>Backlink Required</Label>
                  </div>
                </div>
              </div>
              
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="outline">Cancel</Button>
                </DialogClose>
                <Button onClick={handleAddSource} disabled={!newSource.name || !newSource.source_url}>
                  Add Source
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          
          <Dialog open={showAddContent} onOpenChange={setShowAddContent}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="add-content-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Content
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Add External Content</DialogTitle>
              </DialogHeader>
              
              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Source *</Label>
                    <Select
                      value={newContent.source_id}
                      onValueChange={(v) => setNewContent({...newContent, source_id: v})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select source" />
                      </SelectTrigger>
                      <SelectContent>
                        {sources.map(s => (
                          <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Content Type *</Label>
                    <Select
                      value={newContent.content_type}
                      onValueChange={(v) => setNewContent({...newContent, content_type: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.contentTypes.map(t => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>External ID *</Label>
                    <Input
                      value={newContent.external_id}
                      onChange={(e) => setNewContent({...newContent, external_id: e.target.value})}
                      placeholder="Tweet ID, post ID, etc."
                    />
                  </div>
                  <div>
                    <Label>Original URL *</Label>
                    <Input
                      value={newContent.original_url}
                      onChange={(e) => setNewContent({...newContent, original_url: e.target.value})}
                      placeholder="https://..."
                    />
                  </div>
                </div>
                
                <div>
                  <Label>Title (optional)</Label>
                  <Input
                    value={newContent.title}
                    onChange={(e) => setNewContent({...newContent, title: e.target.value})}
                  />
                </div>
                
                <div>
                  <Label>Text Content</Label>
                  <Textarea
                    value={newContent.text}
                    onChange={(e) => setNewContent({...newContent, text: e.target.value})}
                  />
                </div>
                
                <div>
                  <Label>Image URL (optional)</Label>
                  <Input
                    value={newContent.image_url}
                    onChange={(e) => setNewContent({...newContent, image_url: e.target.value})}
                    placeholder="https://..."
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Author Name</Label>
                    <Input
                      value={newContent.author_name}
                      onChange={(e) => setNewContent({...newContent, author_name: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Author Handle</Label>
                    <Input
                      value={newContent.author_handle}
                      onChange={(e) => setNewContent({...newContent, author_handle: e.target.value})}
                      placeholder="@handle"
                    />
                  </div>
                </div>
              </div>
              
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="outline">Cancel</Button>
                </DialogClose>
                <Button 
                  onClick={handleAddContent} 
                  disabled={!newContent.source_id || !newContent.external_id || !newContent.original_url}
                >
                  Add Content
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="sources">
            <Globe className="w-4 h-4 mr-2" />
            Sources ({sources.length})
          </TabsTrigger>
          <TabsTrigger value="content">
            <Newspaper className="w-4 h-4 mr-2" />
            Content ({content.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="sources">
          {/* Filters */}
          <Card className="mb-6">
            <CardContent className="py-4">
              <div className="flex flex-wrap gap-4 items-end">
                <div className="flex-1 min-w-[150px]">
                  <Label className="text-xs">Status</Label>
                  <Select
                    value={filters.status}
                    onValueChange={(v) => setFilters({...filters, status: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">All</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                      <SelectItem value="pending_review">Pending Review</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1 min-w-[150px]">
                  <Label className="text-xs">Pet Type</Label>
                  <Select
                    value={filters.pet_type}
                    onValueChange={(v) => setFilters({...filters, pet_type: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">All</SelectItem>
                      {categories.petTypes.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1 min-w-[150px]">
                  <Label className="text-xs">Source Type</Label>
                  <Select
                    value={filters.source_type}
                    onValueChange={(v) => setFilters({...filters, source_type: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">All</SelectItem>
                      {categories.sourceTypes.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button variant="outline" onClick={fetchSources}>
                  <Filter className="w-4 h-4 mr-2" />
                  Apply
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Sources List */}
          <div className="grid gap-4">
            {sources.map(source => {
              const SourceIcon = sourceIcons[source.source_type] || Globe;
              return (
                <Card key={source.id} className="overflow-hidden">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      {source.logo_url ? (
                        <img src={source.logo_url} alt={source.name} className="w-12 h-12 rounded-lg object-cover" />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                          <SourceIcon className="w-6 h-6 text-primary" />
                        </div>
                      )}
                      
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-medium">{source.name}</h3>
                          <Badge className={qualityColors[source.quality]}>
                            {source.quality}
                          </Badge>
                          <Badge className={statusColors[source.status]}>
                            {source.status}
                          </Badge>
                        </div>
                        
                        {source.description && (
                          <p className="text-sm text-muted-foreground mb-2">{source.description}</p>
                        )}
                        
                        <div className="flex flex-wrap gap-1 mb-2">
                          {source.pet_types.map(pt => (
                            <Badge key={pt} variant="outline" className="text-xs">{pt}</Badge>
                          ))}
                          {source.content_types.map(ct => (
                            <Badge key={ct} variant="secondary" className="text-xs">{ct}</Badge>
                          ))}
                        </div>
                        
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <TrendingUp className="w-3 h-3" />
                            ELO: {source.elo_rating.toFixed(0)}
                          </span>
                          <span className="flex items-center gap-1">
                            <ThumbsUp className="w-3 h-3" />
                            {source.upvotes}
                          </span>
                          <span className="flex items-center gap-1">
                            <Eye className="w-3 h-3" />
                            {source.view_count} views
                          </span>
                          <span className="flex items-center gap-1">
                            <MousePointer className="w-3 h-3" />
                            {source.click_count} clicks
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => window.open(source.source_url, '_blank')}
                        >
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-destructive"
                          onClick={() => handleDeleteSource(source.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
            
            {sources.length === 0 && (
              <Card className="p-12 text-center">
                <Globe className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-medium mb-2">No sources found</h3>
                <p className="text-sm text-muted-foreground">Add your first external source to get started.</p>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="content">
          <div className="grid gap-4">
            {content.map(item => (
              <Card key={item.id} className="overflow-hidden">
                <CardContent className="p-4">
                  <div className="flex gap-4">
                    {item.image_url && (
                      <img 
                        src={item.image_url} 
                        alt={item.title || 'Content'} 
                        className="w-24 h-24 rounded-lg object-cover"
                      />
                    )}
                    
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline">{item.source_name}</Badge>
                        <Badge variant="secondary">{item.content_type}</Badge>
                        {item.is_featured && (
                          <Badge className="bg-amber-100 text-amber-800">
                            <Star className="w-3 h-3 mr-1" />
                            Featured
                          </Badge>
                        )}
                      </div>
                      
                      {item.title && <h4 className="font-medium mb-1">{item.title}</h4>}
                      {item.text && (
                        <p className="text-sm text-muted-foreground line-clamp-2 mb-2">{item.text}</p>
                      )}
                      
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span>ELO: {item.elo_rating.toFixed(0)}</span>
                        <span className="flex items-center gap-1">
                          <ThumbsUp className="w-3 h-3" />
                          {item.upvotes}
                        </span>
                        <span className="flex items-center gap-1">
                          <Eye className="w-3 h-3" />
                          {item.view_count}
                        </span>
                        <span>{new Date(item.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    
                    <div className="flex flex-col gap-2">
                      <Button
                        variant={item.is_featured ? "default" : "outline"}
                        size="sm"
                        onClick={() => handleToggleFeature(item.id)}
                      >
                        <Star className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => window.open(item.original_url, '_blank')}
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive"
                        onClick={() => handleDeleteContent(item.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            
            {content.length === 0 && (
              <Card className="p-12 text-center">
                <Newspaper className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-medium mb-2">No content yet</h3>
                <p className="text-sm text-muted-foreground">Add content from your sources to populate the feed.</p>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ExternalSourcesAdmin;
